import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createHomeSiteNote,
  getHomeSiteNotes,
  getHomeSiteNotesCounts,
  updateHomeSiteNote,
} from "../../../api/homeSiteNotes/homeSiteNotesApi";
import { Note, Notes } from "../../../api/homeSiteNotes/types";

export const useNotesData = (divcode: string) => {
  return useQuery<Notes>({
    queryKey: ["homeSiteNotes", divcode],
    queryFn: () =>
      getHomeSiteNotes({
        filter: `divisionCode=${divcode},status=true,noteType=homeSiteNotes,docStatus=true`,
      }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
    refetchIntervalInBackground: true,
    enabled: !!divcode,
  });
};

export const useNotesDataByHomesiteId = (divcode: string, homesiteId: string) => {
  return useQuery<Notes>({
    queryKey: ["notesByHomesite", divcode, homesiteId],
    queryFn: () =>
      getHomeSiteNotes({
        filter: `divisionCode=${divcode},homeSiteId=${homesiteId},docStatus=true`,
        order: "DESC",
        orderBy: "createdOnDate",
      }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
    refetchIntervalInBackground: true,
    enabled: !!divcode,
  });
};

export const useNotesCountData = (divcode: string) => {
  return useQuery<any>({
    queryKey: ["homeSiteNotesCounts", divcode],
    queryFn: () =>
      getHomeSiteNotesCounts({
        filter: `divisionCode=${divcode},status=true,noteType=homeSiteNotes,docStatus=true`,
        fields: "homeSiteId",
        groupBy: "homeSiteId",
      }),
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 15, // 15 minutes
    refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
    refetchIntervalInBackground: true,
    enabled: !!divcode,
  });
};

export const useCreateNewNote = (divCode: string, homesiteId: string): UseMutationResult<Note, unknown, Partial<Note>, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createHomeSiteNote,
    onSuccess: (newNote: Note) => {
      queryClient.setQueryData(
        ["notesByHomesite", divCode, homesiteId],
        (currentNotes: Notes | undefined) => {
          if (!currentNotes) return;
          return {
            ...currentNotes,
            data: [newNote, ...currentNotes.data],
          };
        }
      );
      queryClient.setQueryData(["homeSiteNotes", divCode], (oldData: Notes) => {
        if (!oldData) return;
        const returnNote = {
          ...oldData,
          data: [newNote, ...oldData.data],
        };
        return returnNote;
      });
    },
  });
};

export const useUpdateNotes = (divCode: string, homesiteId: string): UseMutationResult<Note, unknown, Partial<Note>, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateHomeSiteNote,
    onSuccess: (updatedNote: Note, updatedNoteData) => {
      queryClient.setQueryData(
        ["notesByHomesite", divCode, homesiteId],
        (currentNotes: Notes | undefined) => {
          if (!currentNotes) return;
          return {
            ...currentNotes,
            data: currentNotes?.data?.map((note: Note) => {
              const updatedNotes =
                note.id === updatedNote.id ? updatedNote : note;
              return updatedNotes;
            }),
          };
        }
      );
      queryClient.setQueryData(
        ["homeSiteNotes", divCode],
        (currentNotes: Notes | undefined) => {
          if (!currentNotes) return;
          const updatedData = currentNotes?.data.map((note: Note) => {
            return note.id === updatedNoteData.id ? updatedNote : note;
          });
          const returnData = { ...currentNotes, data: updatedData };
          return returnData;
        }
      );
    },
    onError: (error) => {
      console.error("Error updating Note:", error);
    },
  });
};
